import { useCallback, useEffect, useRef, useState } from "react"
import React from "react"
import { toast } from "sonner"
import { AddonsSection } from "@/components/AddonsSection"
import { CustomDatesModal } from "@/components/CustomDatesModal"
import { FlightSection } from "@/components/FlightSection"
import { Footer } from "@/components/Footer"
import { HeroSection } from "@/components/HeroSection"
import { HotelSection } from "@/components/HotelSection"
import { TRexLoader } from "@/components/TRexLoader"
import { MobileSummaryBar } from "@/components/MobileSummaryBar"
import { PackageSummary } from "@/components/PackageSummary"
import { useBooking } from "@/context/BookingContext"
import { dispatchApiError } from "@/lib/api/dispatch"
import { useInView } from "@/hooks/useInView"
import { useScrollToSection } from "@/hooks/useScrollToSection"
import { useBookingUrlRestore, useBookingUrlWriter } from "@/hooks/useBookingUrl"
import { searchHotelOffers, searchFlightOffers } from "@/lib/api/search"
import { getHotelAddOns } from "@/lib/api/client"
import { AuthError } from "@/lib/api/errors"
import { getSelectedStayPeriod, getGrandTotal } from "@/lib/booking"
import type {
  BookingRequestFormValues,
  FlightOptionView,
} from "@/types/booking"
import type { HotelOption } from "@/lib/api/types"
import {
  pushSearch,
  pushViewItemList,
  pushSelectItem,
} from "@/lib/ga4"
import { phCapture } from "@/lib/posthog"

// Optimistic 0-100 progress derived from the existing loading state phases.
// The bar creeps smoothly toward a ceiling between real milestones so it
// never looks frozen, then jumps to the real anchor when the API responds.
function useOptimisticProgress(state: {
  offersLoading: boolean
  offersReady: boolean
  flightOffersReady: boolean
  isFlightOnly: boolean
}) {
  const target = state.flightOffersReady
    ? 100
    : state.offersReady
      ? 55
      : state.offersLoading
        ? 8
        : 0

  const ceiling = state.flightOffersReady
    ? 100
    : state.offersReady
      ? 90
      : state.offersLoading
        ? state.isFlightOnly ? 90 : 45
        : 0

  const [value, setValue] = React.useState(0)
  useEffect(() => {
    let raf = 0
    const rate = state.isFlightOnly ? 0.003 : 0.02
    const tick = () => {
      setValue((cur) => {
        const goal = Math.max(target, cur)
        const next = cur + (ceiling - cur) * rate
        return Math.min(ceiling, Math.max(goal, next))
      })
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [target, ceiling, state.isFlightOnly])

  return Math.round(value)
}

export default function Index({ syncUrl = false }: { syncUrl?: boolean }) {
  const { state, dispatch } = useBooking()
  const { scrollTo } = useScrollToSection()

  const progress = useOptimisticProgress({
    offersLoading: state.offersStatus === "loading",
    offersReady: state.offersStatus === "success",
    flightOffersReady: state.flightOffersStatus === "success",
    isFlightOnly: state.packageType === "flight-only",
  })
  const [bookingRequestOpen, setBookingRequestOpen] = useState(false)
  const [bookingRequestInitialValues, setBookingRequestInitialValues] =
    useState<Partial<BookingRequestFormValues>>()

  const hotelRef = useRef<HTMLElement>(null)
  const flightRef = useRef<HTMLElement>(null)
  const addonsRef = useRef<HTMLElement>(null)
  const [setInlineSummaryRef, isInlineSummaryVisible] = useInView<HTMLDivElement>({
    rootMargin: "0px 0px -64px 0px",
  })

  function openBookingRequest(
    initialValues?: Partial<BookingRequestFormValues>
  ) {
    setBookingRequestInitialValues({
      firstName: state.travelerInfo.firstName || "",
      lastName: state.travelerInfo.lastName || "",
      email: state.travelerInfo.email || "",
      phone: state.travelerInfo.phone || "",
      destinationAirport: state.destinationAirport?.code ?? state.event?.airport ?? "",
      ...initialValues,
    })
    setBookingRequestOpen(true)
  }

  async function handleSearch(overrides?: {
    shared?: boolean
  }) {
    const selectedStayPeriod = getSelectedStayPeriod(state)
    const shared =
      overrides?.shared ?? (state.sharedRoom === true ? true : undefined)

    const isFlightOnly = state.packageType === "flight-only"
    const hasCustomDates =
      isFlightOnly &&
      state.customFlightDepartureDate &&
      state.customFlightReturnDate

    if (!hasCustomDates && !selectedStayPeriod?.stayStartDate) {
      toast.error("Select travel dates to continue.")
      return
    }

    if (isFlightOnly) {
      const stayStartDate = hasCustomDates
        ? state.customFlightDepartureDate!
        : selectedStayPeriod!.stayStartDate
      const stayEndDate = hasCustomDates
        ? state.customFlightReturnDate!
        : selectedStayPeriod!.stayEndDate

      // GA4: search — fires when the user successfully starts a flight search
      pushSearch(state.event?.slug ?? "")
      // PostHog: search
      phCapture('search', {
        event_slug: state.event?.slug ?? "",
        package_type: state.packageType,
        passenger_count: state.passengerCount,
        departure_airports: state.departureAirports.map((a) => a.code),
      })

      dispatch({ type: "SET_STEP", step: "loading" })
      dispatch({ type: "SET_OFFERS_LOADING" })
      try {
        const flightResult = await searchFlightOffers({
          eventId: state.event?.id ?? "",
          stayStartDate,
          stayEndDate,
          passengerCount: state.passengerCount,
          departureAirports: state.departureAirports.map((a) => a.code),
          destination: state.destinationAirport?.code ?? undefined,
          mode: "flight",
        })

        dispatch({
          type: "SET_FLIGHT_OFFERS_SUCCESS",
          flightOffers: flightResult.flightOffers,
          flightOptions: flightResult.flightOptions,
        })

        // GA4: view_item_list — fires when flight results are displayed
        pushViewItemList(
          flightResult.flightOptions,
          flightResult.flightOffers.basePackagePricePerPerson,
          state.passengerCount,
          state.currency,
          state.event?.slug ?? "",
          state.event?.name ?? ""
        )
        // PostHog: view_flight_list
        phCapture('view_flight_list', {
          event_slug: state.event?.slug ?? "",
          flight_count: flightResult.flightOptions.length,
          passenger_count: state.passengerCount,
          currency: state.currency,
          base_price: flightResult.flightOffers.basePackagePricePerPerson,
        })

        dispatch({ type: "SET_STEP", step: "flight-selection" })
        scrollTo(flightRef)
      } catch (error) {
        if (error instanceof AuthError) {
          window.location.href = "/login"
          return
        }
        dispatchApiError(dispatch, error, "flights")
        dispatch({ type: "SET_STEP", step: "idle" })
      }
      return
    }

    dispatch({ type: "SET_OFFERS_LOADING" })
    dispatch({ type: "SET_STEP", step: "loading" })

    // GA4: search — fires when the user successfully starts a search that
    // will result in flight options being displayed (flight-hotel mode)
    pushSearch(state.event?.slug ?? "")
    // PostHog: search
    phCapture('search', {
      event_slug: state.event?.slug ?? "",
      package_type: state.packageType,
      passenger_count: state.passengerCount,
      departure_airports: state.departureAirports.map((a) => a.code),
    })

    try {
      const result = await searchHotelOffers({
        eventId: state.event?.id ?? "",
        stayStartDate: selectedStayPeriod!.stayStartDate,
        stayEndDate: selectedStayPeriod!.stayEndDate,
        passengerCount: state.passengerCount,
        packageType: state.packageType,
        shared,
      })

      dispatch({
        type: "SET_OFFERS_SUCCESS",
        offers: result.offers,
      })

      dispatch({ type: "SET_STEP", step: "hotel-selection" })
      scrollTo(hotelRef)

      dispatch({ type: "SET_HOTEL_ADDONS_LOADING" })
      try {
        const hotelIds = result.offers.hotelOptions.map((h) => h.hotelId)
        if (hotelIds.length > 0) {
          const { hotels } = await getHotelAddOns(hotelIds, selectedStayPeriod!)
          const byHotel = Object.fromEntries(
            hotels.map((h) => [h.hotelId, h.addOnOptions])
          )
          dispatch({ type: "SET_HOTEL_ADDONS_SUCCESS", hotelAddOns: byHotel })
        } else {
          dispatch({ type: "SET_HOTEL_ADDONS_SUCCESS", hotelAddOns: {} })
        }
      } catch (error) {
        if (error instanceof AuthError) {
          window.location.href = "/login"
          return
        }
        dispatchApiError(dispatch, error, "addons")
      }
    } catch (error) {
      if (error instanceof AuthError) {
        window.location.href = "/login"
        return
      }
      dispatchApiError(dispatch, error, "offers")
      dispatch({ type: "SET_STEP", step: "idle" })
    }
  }

  useBookingUrlRestore({
    state,
    dispatch,
    onSearch: () => void handleSearch(),
    onSelectHotel: (hotel) =>
      handleHotelSelect(hotel, { track: false, scroll: false }),
    onSelectFlight: (flight) =>
      handleFlightSelect(flight, { track: false, scroll: false }),
    enabled: syncUrl,
  })
  useBookingUrlWriter(state, syncUrl)

  // Both flags are off when the selection came from a link rather than a click.
  // The step still has to advance so the rest of the package is on the page,
  // but nobody chose anything just now, so recording a selection would be
  // wrong — and someone opening a shared link should start at the top of the
  // event, not be dropped halfway down it.
  function handleHotelSelect(
    hotel: HotelOption,
    { track = true, scroll = true } = {}
  ) {
    dispatch({ type: "SELECT_HOTEL", hotel })

    // PostHog: hotel_selected
    if (track) phCapture('hotel_selected', {
      event_slug: state.event?.slug ?? "",
      hotel_id: hotel.hotelId,
      hotel_name: hotel.name,
      hotel_stars: hotel.stars,
      passenger_count: state.passengerCount,
      currency: state.currency,
    })

    if (state.packageType === "flight-hotel") {
      dispatch({ type: "SET_STEP", step: "flight-selection" })
      if (scroll) scrollTo(flightRef)
      return
    }

    dispatch({ type: "SET_STEP", step: "addons" })
    if (scroll) scrollTo(addonsRef)
  }

  function handleFlightSelect(
    flight: FlightOptionView,
    { track = true, scroll = true } = {}
  ) {
    dispatch({ type: "SELECT_FLIGHT", flight })
    dispatch({ type: "SET_STEP", step: "addons" })
    if (scroll) scrollTo(addonsRef)

    if (!track) return

    // GA4: select_item — fires when the user clicks/selects a flight option
    pushSelectItem(
      flight,
      state.flightBasePackagePrice ?? state.offers?.basePackagePricePerPerson ?? 0,
      state.passengerCount,
      state.currency,
      state.event?.slug ?? "",
      state.event?.name ?? ""
    )
    // PostHog: flight_selected
    phCapture('flight_selected', {
      event_slug: state.event?.slug ?? "",
      flight_id: flight.pelijeeFlightId,
      route: flight.outbound.originAirportCode + '-' + flight.outbound.destinationAirportCode,
      price_per_person: Math.max(
        (state.flightBasePackagePrice ?? state.offers?.basePackagePricePerPerson ?? 0) +
        flight.priceDiffPerPerson,
        0
      ),
      passenger_count: state.passengerCount,
      currency: state.currency,
    })
  }

  function handleContinueToCheckout() {
    dispatch({ type: "SET_STEP", step: "checkout" })
    window.scrollTo({ top: 0, behavior: "smooth" })

    // PostHog: checkout_started — all selections are final at this point
    phCapture('checkout_started', {
      event_slug: state.event?.slug ?? "",
      package_type: state.packageType,
      passenger_count: state.passengerCount,
      currency: state.currency,
      value: getGrandTotal(state),
      has_flight: state.selectedFlight !== null,
      has_hotel: state.selectedHotel !== null,
      addon_count: Object.values(state.selectedAddOns).filter((q) => q > 0).length,
    })
  }

  const handleSearchRef = useRef(handleSearch)
  useEffect(() => {
    handleSearchRef.current = handleSearch
  })

  useEffect(() => {
    if (!state.needsOffersRefresh) return
    if (state.offersStatus === "loading") return

    handleSearchRef.current()
  }, [state.needsOffersRefresh, state.offersStatus])

  useEffect(() => {
    if (state.flightOffersStatus !== "loading") return

    const isFlightHotel = state.packageType === "flight-hotel"
    const isFlightOnly = state.packageType === "flight-only"

    if (!isFlightHotel && !isFlightOnly) return

    if (isFlightHotel && !state.offers) return

    const hasCustomDates =
      isFlightOnly &&
      state.customFlightDepartureDate &&
      state.customFlightReturnDate

    const stayPeriod = getSelectedStayPeriod(state)
    if (!hasCustomDates && !stayPeriod) return

    const stayStartDate = hasCustomDates
      ? state.customFlightDepartureDate!
      : stayPeriod!.stayStartDate
    const stayEndDate = hasCustomDates
      ? state.customFlightReturnDate!
      : stayPeriod!.stayEndDate

    let active = true

    searchFlightOffers({
      eventId: state.event?.id ?? "",
      stayStartDate,
      stayEndDate,
      passengerCount: state.passengerCount,
      departureAirports: state.departureAirports.map((a) => a.code),
      destination: state.destinationAirport?.code ?? undefined,
      mode: isFlightOnly ? "flight" : "hotel_flight",
    })
      .then((result) => {
        if (!active) return
        dispatch({
          type: "SET_FLIGHT_OFFERS_SUCCESS",
          flightOffers: result.flightOffers,
          flightOptions: result.flightOptions,
        })

        // GA4: view_item_list — fires when flight results are displayed
        // (flight-hotel mode: results arrive via this effect, not handleSearch)
        pushViewItemList(
          result.flightOptions,
          result.flightOffers.basePackagePricePerPerson,
          state.passengerCount,
          state.currency,
          state.event?.slug ?? "",
          state.event?.name ?? ""
        )
        // PostHog: view_flight_list
        phCapture('view_flight_list', {
          event_slug: state.event?.slug ?? "",
          flight_count: result.flightOptions.length,
          passenger_count: state.passengerCount,
          currency: state.currency,
          base_price: result.flightOffers.basePackagePricePerPerson,
        })
      })
      .catch((error) => {
        if (!active) return
        if (error instanceof AuthError) {
          window.location.href = "/login"
          return
        }
        dispatchApiError(dispatch, error, "flights")
      })

    return () => {
      active = false
    }
  }, [state.flightOffersStatus, state.packageType, state.offers])

  const retryAddOns = useCallback(() => {
    if (!state.offers || state.hotelAddOnsStatus !== "error") return
    dispatch({ type: "SET_HOTEL_ADDONS_LOADING" })
    const hotelIds = state.offers.hotelOptions.map((h) => h.hotelId)
    if (hotelIds.length === 0) {
      dispatch({ type: "SET_HOTEL_ADDONS_SUCCESS", hotelAddOns: {} })
      return
    }
    getHotelAddOns(hotelIds, getSelectedStayPeriod(state) ?? undefined)
      .then(({ hotels }) => {
        const byHotel = Object.fromEntries(
          hotels.map((h) => [h.hotelId, h.addOnOptions])
        )
        dispatch({ type: "SET_HOTEL_ADDONS_SUCCESS", hotelAddOns: byHotel })
      })
      .catch((error) => {
        dispatchApiError(dispatch, error, "addons")
      })
  }, [state.offers, state.hotelAddOnsStatus, dispatch])

  const stepOrder: string[] =
    state.packageType === "flight-hotel"
      ? [
          "idle",
          "loading",
          "hotel-selection",
          "flight-selection",
          "addons",
          "checkout",
        ]
      : state.packageType === "flight-only"
        ? ["idle", "loading", "flight-selection", "addons", "checkout"]
        : ["idle", "loading", "hotel-selection", "addons", "checkout"]

  const currentIdx = stepOrder.indexOf(state.currentStep)
  const jumpedToFlights =
    state.currentStep === "flight-selection" && state.selectedHotel === null
  const showHotels =
    state.packageType !== "flight-only" &&
    !jumpedToFlights &&
    currentIdx >= stepOrder.indexOf("hotel-selection")
  const showFlights =
    (state.packageType === "flight-hotel" &&
      state.selectedHotel !== null &&
      (currentIdx >= stepOrder.indexOf("flight-selection") ||
        currentIdx >= stepOrder.indexOf("hotel-selection"))) ||
    (state.packageType === "flight-only" &&
      currentIdx >= stepOrder.indexOf("flight-selection"))
  const showAddons = currentIdx >= stepOrder.indexOf("addons")
  const showSummary = currentIdx >= stepOrder.indexOf(
    state.packageType === "flight-only" ? "flight-selection" : "hotel-selection"
  )
  const showSummaryContent =
    state.selectedHotel !== null ||
    (state.packageType === "flight-only" && state.selectedFlight !== null)

  return (
    <div className="min-h-screen">
      {state.isLoading && <TRexLoader variant="overlay" value={progress} />}

      <div className="relative">
        <HeroSection
          onSearch={handleSearch}
          onOpenBookingRequest={openBookingRequest}
        />

        {(showHotels || showFlights) && (
          <div className="mx-auto max-w-7xl px-4 @min-[64rem]:grid @min-[64rem]:grid-cols-[1fr_320px] @min-[64rem]:gap-8">
            <div className="flex flex-col">
              {showHotels && (
                <HotelSection
                  ref={hotelRef}
                  onHotelSelect={handleHotelSelect}
                  onOpenBookingRequest={() =>
                    openBookingRequest({
                      departureAirport: state.departureAirports[0]?.code ?? "",
                      travelFrom:
                        getSelectedStayPeriod(state)?.stayStartDate ?? "",
                      travelTo: getSelectedStayPeriod(state)?.stayEndDate ?? "",
                      travelers: state.passengerCount,
                    })
                  }
                  onRetry={handleSearch}
                />
              )}

              {showFlights && (
                <FlightSection
                  ref={flightRef}
                  onFlightSelect={handleFlightSelect}
                  isFlightOnly={state.packageType === "flight-only"}
                  onEditAirport={() =>
                    window.scrollTo({ top: 0, behavior: "smooth" })
                  }
                  onHotelOnly={() => {
                    dispatch({
                      type: "SET_PACKAGE_TYPE",
                      packageType: "hotel-only",
                      preserveResults: true,
                    })
                    dispatch({ type: "SET_STEP", step: "hotel-selection" })
                    scrollTo(hotelRef, 100)
                  }}
                  onCustomForm={() =>
                    openBookingRequest({
                      departureAirport: state.departureAirports[0]?.code ?? "",
                      travelFrom:
                        getSelectedStayPeriod(state)?.stayStartDate ?? "",
                      travelTo: getSelectedStayPeriod(state)?.stayEndDate ?? "",
                      travelers: state.passengerCount,
                    })
                  }
                />
              )}

              {showAddons && (
                <AddonsSection
                  ref={addonsRef}
                  onContinue={handleContinueToCheckout}
                  inlineSummaryRef={setInlineSummaryRef}
                  showInlineSummary={showSummaryContent}
                  onRetryAddOns={retryAddOns}
                />
              )}
            </div>

            {showSummary && (
              <aside className="hidden @min-[64rem]:block">
                <div className="sticky top-8 pt-9">
                  <PackageSummary />
                </div>
              </aside>
            )}
          </div>
        )}
      </div>

      {showSummaryContent && (
        <MobileSummaryBar hidden={isInlineSummaryVisible} />
      )}

      <CustomDatesModal
        open={bookingRequestOpen}
        onOpenChange={setBookingRequestOpen}
        initialValues={bookingRequestInitialValues}
        packageType={state.packageType}
      />

      <Footer eventSlug={state.event?.slug ?? undefined} />
    </div>
  )
}
